// SPDX-FileCopyrightText: 2017-2022 City of Espoo
//
// SPDX-License-Identifier: LGPL-2.1-or-later

import sortBy from 'lodash/sortBy'
import type { Dispatch, SetStateAction } from 'react'
import React, { useContext, useState } from 'react'
import styled from 'styled-components'

import type { Action } from 'lib-common/generated/action'
import type { UnitBackupCare } from 'lib-common/generated/api-types/backupcare'
import type {
  Caretakers,
  Daycare,
  DaycareGroup
} from 'lib-common/generated/api-types/daycare'
import type { NekkuUnitNumber } from 'lib-common/generated/api-types/nekku'
import type { OccupancyResponse } from 'lib-common/generated/api-types/occupancy'
import type { DaycarePlacementWithDetails } from 'lib-common/generated/api-types/placement'
import type { GroupId } from 'lib-common/generated/api-types/shared'
import type LocalDate from 'lib-common/local-date'
import { useQueryResult } from 'lib-common/query'
import type { UUID } from 'lib-common/types'
import AddButton from 'lib-components/atoms/buttons/AddButton'
import { IconOnlyButton } from 'lib-components/atoms/buttons/IconOnlyButton'
import { InlineInternalLinkButton } from 'lib-components/atoms/buttons/InlineLinkButton'
import { FixedSpaceRow } from 'lib-components/layout/flex-helpers'
import { H2, Label } from 'lib-components/typography'
import { Gap } from 'lib-components/white-space'
import { faAngleDown, faAngleUp } from 'lib-icons'

import { useTranslation } from '../../../state/i18n'
import { UIContext } from '../../../state/ui'
import type {
  DaycareGroupPlacementDetailed,
  UnitChildrenCapacityFactors
} from '../../../types/unit'
import { flatMapGroupPlacements } from '../../../types/unit'
import type { UnitFilters } from '../../../utils/UnitFilters'
import { permittedReportsQuery } from '../../reports/queries'
import UnitDataFilters from '../UnitDataFilters'

import Group from './groups/Group'
import GroupModal from './groups/GroupModal'
import GroupTransferModal from './groups/group/GroupTransferModal'
import BackupCareGroupModal from './missing-group-placements/BackupCareGroupModal'

function renderGroups(
  unit: Daycare,
  filters: UnitFilters,
  groups: DaycareGroup[],
  groupLastPlacementDates: Partial<Record<GroupId, LocalDate>>,
  groupPermittedActions: Record<UUID, Action.Group[] | undefined>,
  placements: DaycarePlacementWithDetails[],
  permittedBackupCareActions: Partial<Record<UUID, Action.BackupCare[]>>,
  permittedGroupPlacementActions: Partial<
    Record<UUID, Action.GroupPlacement[]>
  >,
  backupCares: UnitBackupCare[],
  groupCaretakers: Partial<Record<string, Caretakers>>,
  onTransferRequested: (
    placement: DaycareGroupPlacementDetailed | UnitBackupCare
  ) => void,
  openGroups: Record<string, boolean>,
  setOpenGroups: React.Dispatch<React.SetStateAction<Record<string, boolean>>>,
  unitChildrenCapacities: UnitChildrenCapacityFactors[],
  nekkuUnits: NekkuUnitNumber[],
  confirmedOccupancies?: Partial<Record<string, OccupancyResponse>>,
  realizedOccupancies?: Partial<Record<string, OccupancyResponse>>
) {
  const groupsWithPlacements = groups.map((group) => ({
    ...group,
    placements: flatMapGroupPlacements(placements).filter(
      (it) => it.groupId === group.id
    ),
    lastPlacementDate: groupLastPlacementDates[group.id] ?? null,
    backupCares: backupCares.filter((it) => it.group?.id === group.id)
  }))
  const sortedGroups = sortBy(groupsWithPlacements, [
    (g) => g.name.toLowerCase()
  ])

  return (
    <div data-qa="daycare-groups-list">
      {sortedGroups.map((group) => (
        <Group
          unit={unit}
          filters={filters}
          group={group}
          caretakers={groupCaretakers[group.id]}
          confirmedOccupancy={confirmedOccupancies?.[group.id]}
          realizedOccupancy={realizedOccupancies?.[group.id]}
          key={group.id}
          onTransferRequested={onTransferRequested}
          open={openGroups[group.id]}
          toggleOpen={() =>
            setOpenGroups((current) => ({
              ...current,
              [group.id]: !current[group.id]
            }))
          }
          permittedActions={groupPermittedActions[group.id] ?? []}
          permittedBackupCareActions={permittedBackupCareActions}
          permittedGroupPlacementActions={permittedGroupPlacementActions}
          unitChildrenCapacityFactors={unitChildrenCapacities}
          nekkuUnits={nekkuUnits}
        />
      ))}
    </div>
  )
}

type Props = {
  unit: Daycare
  permittedActions: Action.Unit[]
  filters: UnitFilters
  setFilters: (filters: UnitFilters) => void
  groups: DaycareGroup[]
  groupLastPlacementDates: Partial<Record<GroupId, LocalDate>>
  placements: DaycarePlacementWithDetails[]
  backupCares: UnitBackupCare[]
  groupPermittedActions: Record<UUID, Action.Group[] | undefined>
  groupCaretakers: Partial<Record<string, Caretakers>>
  groupConfirmedOccupancies?: Partial<Record<string, OccupancyResponse>>
  groupRealizedOccupancies?: Partial<Record<string, OccupancyResponse>>
  openGroups: Record<string, boolean>
  setOpenGroups: Dispatch<SetStateAction<Record<string, boolean>>>
  permittedBackupCareActions: Partial<Record<UUID, Action.BackupCare[]>>
  permittedGroupPlacementActions: Partial<Record<UUID, Action.GroupPlacement[]>>
  unitChildrenCapacityFactors: UnitChildrenCapacityFactors[]
  nekkuUnits: NekkuUnitNumber[]
}

export default React.memo(function Groups({
  unit,
  permittedActions,
  filters,
  setFilters,
  groups,
  groupLastPlacementDates,
  placements,
  backupCares,
  groupPermittedActions,
  groupCaretakers,
  groupConfirmedOccupancies,
  groupRealizedOccupancies,
  openGroups,
  setOpenGroups,
  permittedBackupCareActions,
  permittedGroupPlacementActions,
  unitChildrenCapacityFactors,
  nekkuUnits
}: Props) {
  const { i18n } = useTranslation()
  const { uiMode, toggleUiMode } = useContext(UIContext)
  const [transferredPlacement, setTransferredPlacement] = useState<
    DaycareGroupPlacementDetailed | UnitBackupCare | null
  >(null)

  const allGroupsAreOpen = groups.every(({ id }) => openGroups[id])

  const toggleAllGroups = () => {
    if (groups) {
      if (allGroupsAreOpen) {
        setOpenGroups({})
      } else {
        setOpenGroups(Object.fromEntries(groups.map(({ id }) => [id, true])))
      }
    }
  }

  const permittedReportsResult = useQueryResult(permittedReportsQuery())
  const canSeeFamilyContactsReport = permittedReportsResult
    .map((reports) => reports.includes('FAMILY_CONTACT'))
    .getOrElse(false)

  return (
    <>
      <TitleBar
        data-qa="groups-title-bar"
        data-status={allGroupsAreOpen ? 'open' : 'closed'}
      >
        <TitleContainer onClick={toggleAllGroups}>
          <H2 $fitted>{i18n.unit.groups.title}</H2>
          <Gap $size="XL" $horizontal />
          <IconOnlyButton
            icon={allGroupsAreOpen ? faAngleUp : faAngleDown}
            size="L"
            color="gray"
            data-qa="toggle-all-groups-collapsible"
            aria-label={allGroupsAreOpen ? i18n.common.close : i18n.common.open}
          />
        </TitleContainer>
        <FixedSpaceRow $spacing="L" $alignItems="center">
          {canSeeFamilyContactsReport && (
            <InlineInternalLinkButton
              to={`/units/${unit.id}/family-contacts`}
              text={i18n.unit.groups.familyContacts}
              data-qa="open-family-contacts-button"
            />
          )}
          {permittedActions.includes('CREATE_GROUP') ? (
            <AddButton
              text={i18n.unit.groups.create}
              onClick={() => toggleUiMode('create-new-daycare-group')}
              disabled={uiMode === 'create-new-daycare-group'}
              flipped
            />
          ) : null}
        </FixedSpaceRow>
      </TitleBar>
      <Gap $size="s" />
      <FixedSpaceRow $alignItems="center">
        <Label>{i18n.unit.filters.title}</Label>
        <UnitDataFilters filters={filters} setFilters={setFilters} />
      </FixedSpaceRow>
      <Gap $size="s" />
      {uiMode === 'create-new-daycare-group' && <GroupModal unitId={unit.id} />}
      {uiMode === 'group-transfer' && transferredPlacement && (
        <>
          {'type' in transferredPlacement ? (
            <GroupTransferModal
              unitId={unit.id}
              placement={transferredPlacement}
              groups={groups}
            />
          ) : (
            <BackupCareGroupModal
              unitId={unit.id}
              backupCare={transferredPlacement}
              groups={groups}
            />
          )}
        </>
      )}
      {renderGroups(
        unit,
        filters,
        groups,
        groupLastPlacementDates,
        groupPermittedActions,
        placements,
        permittedBackupCareActions,
        permittedGroupPlacementActions,
        backupCares,
        groupCaretakers,
        (placement) => {
          setTransferredPlacement(placement)
          toggleUiMode('group-transfer')
        },
        openGroups,
        setOpenGroups,
        unitChildrenCapacityFactors,
        nekkuUnits,
        groupConfirmedOccupancies,
        groupRealizedOccupancies
      )}
    </>
  )
})

const TitleBar = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`

const TitleContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  flex-grow: 1;
`
